import { io } from "socket.io-client";

class WebSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.connectionPromise = null;
  }

  setToken(token) {
    if (this.socket) {
      this.socket.auth = { token };
    }
  }

  connect(token) {
    if (this.socket?.connected) {
      this.setToken(token);
      return Promise.resolve(this.socket);
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    const WS_URL = process.env.REACT_APP_SOCKET_URL;

    this.connectionPromise = new Promise((resolve, reject) => {
      this.socket = io(WS_URL, {
        auth: { token },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ["websocket", "polling"],
      });

      // Connection successful
      this.socket.on("connect", () => {
        this.connected = true;
        this.connectionPromise = null;
        resolve(this.socket);
      });

      // Connection error
      this.socket.on("connect_error", (error) => {
        this.connectionPromise = null;
        reject(error);
      });

      // Disconnection
      this.socket.on("disconnect", (reason) => {
        this.connected = false;
      });

      // Timeout if connection takes too long
      setTimeout(() => {
        if (!this.connected) {
          this.connectionPromise = null;
          reject(new Error("Connection timeout"));
        }
      }, 10000);
    });

    return this.connectionPromise;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.connectionPromise = null;
    }
  }

  getSocket() {
    return this.socket;
  }

  isConnected() {
    return this.connected && this.socket?.connected;
  }

  subscribeToProfile(profileId) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error("WebSocket not connected"));
        return;
      }

      const confirmationHandler = (data) => {
        if (data.profileId === profileId) {
          this.socket.off("subscription_confirmed", confirmationHandler);
          resolve(data);
        }
      };

      const errorHandler = (error) => {
        this.socket.off("subscription_confirmed", confirmationHandler);
        this.socket.off("error", errorHandler);
        reject(error);
      };

      // Set up listeners
      this.socket.on("subscription_confirmed", confirmationHandler);
      this.socket.on("error", errorHandler);

      // Emit subscription request
      this.socket.emit("subscribe_profile", { profileId });

      // Timeout after 5 seconds
      setTimeout(() => {
        this.socket.off("subscription_confirmed", confirmationHandler);
        this.socket.off("error", errorHandler);
        reject(new Error("Subscription confirmation timeout"));
      }, 5000);
    });
  }

  unsubscribeFromProfile(profileId) {
    if (!this.socket?.connected) {
      return;
    }

    this.socket.emit("unsubscribe_profile", { profileId });
  }

  // Wait for connection helper
  waitForConnection(timeout = 5000) {
    return new Promise((resolve, reject) => {
      if (this.isConnected()) {
        resolve(this.socket);
        return;
      }

      const checkInterval = setInterval(() => {
        if (this.isConnected()) {
          clearInterval(checkInterval);
          resolve(this.socket);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error("Connection timeout"));
      }, timeout);
    });
  }
}

const websocketService = new WebSocketService();

export default websocketService;
